import { HttpClient } from '@angular/common/http'
import { Component, OnInit, ViewChild, inject } from '@angular/core'
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs'
import { map, shareReplay } from 'rxjs/operators'

import { HuntPackageComponent } from '../../../../../../shared/components/hunt-package/hunt-package.component'
import { CompanyService } from '../../../../../../shared/services/company.service'
import { HuntPackageService } from '../../../../../../shared/services/hunt-package.service'

export interface MITRETactics {
  created_by_ref: string
  object_marking_refs: string[]
  external_references: ExternalReference[]
  name: string
  description: string
  id: string
  type: string
  modified: Date
  created: Date
  x_mitre_shortname: string
  children: Child[]
}
export interface Child {
  id: string
  description: string
  name: string
  created_by_ref: string
  object_marking_refs: string[]
  external_references: ExternalReference[]
  type: string
  kill_chain_phases: KillChainPhase[]
  modified: Date
  created: Date
  x_mitre_platforms: string[]
  x_mitre_is_subtechnique: boolean
  x_mitre_version: string
  x_mitre_detection: string
  x_mitre_data_sources: string[]
  sub_techniques?: Child[]
  revoked?: boolean
}

export interface ExternalReference {
  source_name: string
  external_id?: string
  url: string
  description?: string
}
export interface KillChainPhase {
  kill_chain_name: string
  phase_name: string
}

export interface DeployedDeploys {
  deployedTactics: string[]
  deployedTechniques: string[]
}

export interface StagedDeploys {
  stagedTactics: string[]
  stagedTechniques: string[]
}

export const MITRETacticArray = [
  'reconnaissance',
  'resource-development',
  'initial-access',
  'execution',
  'persistence',
  'privilege-escalation',
  'defense-evasion',
  'credential-access',
  'discovery',
  'lateral-movement',
  'collection',
  'exfiltration',
  'command-and-control',
  'impact',
]

/**
 *
 * @param mitreJSON MITRE enterprise-attack.json data
 * @returns Transformed payload for component table use
 */
export function processMitreEnterpriseJSON(mitreJSON): MITRETactics[] {
  const mitreTechniques = mitreJSON['objects'].filter((techniques) => {
    return techniques.type === 'attack-pattern'
  })

  const subTechniques = mitreJSON['objects'].filter((relationshipObject) => {
    return relationshipObject.relationship_type === 'subtechnique-of'
  })

  const tactics = MITRETacticArray.map((element) => {
    const tactic = mitreJSON['objects'].find((tacticObject) => {
      return tacticObject.x_mitre_shortname === element
    })

    //Get All Techniques excluding Sub-Techniques
    const children = mitreTechniques.filter((phaseNames) => {
      return phaseNames.kill_chain_phases?.find((tacticName) => {
        return (
          tacticName.phase_name === tactic.x_mitre_shortname &&
          !phaseNames.x_mitre_deprecated &&
          !phaseNames.x_mitre_is_subtechnique
        )
      })
    })

    // Map all subtechniques to parent techniques, and filter out revoked children
    const finalTechniques = children
      .map((element) => {
        // Skip the element if it's revoked
        if (element.revoked) {
          return null // Return null for revoked elements so we can filter them out later
        }

        // Find subtechniques related to this parent technique
        const subtechniques = subTechniques.filter((rel) => {
          return rel.target_ref === element.id
        })
        // Map subtechniques to actual subtechnique objects
        const final = subtechniques.map((subTech) => {
          const temp = mitreJSON['objects'].find((techObject) => {
            return techObject.id === subTech.source_ref
          })
          return temp
        })
        element['sub_techniques'] = final
        return element
      })
      .filter((element) => element !== null) // Filter out the revoked (null) elements
    tactic['children'] = finalTechniques
    return tactic
  })
  return tactics
}

// Allow caching data between component init/destroy cycles
let cachedMitreSubject: ReplaySubject<MITRETactics[]>

@Component({
  standalone: false,
  selector: 'cyborg-mitre',
  templateUrl: './mitre.component.html',
  styleUrls: ['./mitre.component.scss'],
})
export class MitreComponent implements OnInit {
  private huntPackageService = inject(HuntPackageService)
  private companyService = inject(CompanyService)
  private httpClient = inject(HttpClient)

  @ViewChild(HuntPackageComponent) public huntPackage: HuntPackageComponent
  public headers = Array.from(MITRETacticArray).map((element) => {
    return element.replace(/-/gi, ' ')
  })

  public static readonly mitreEnterpriseJsonUrl: string =
    'https://raw.githubusercontent.com/mitre/cti/ATT%26CK-v12.0/enterprise-attack/enterprise-attack.json'

  public tactics$: ReplaySubject<MITRETactics[]>
  public rows$: Observable<Array<number>>
  public cyborgMitreFilter$: Observable<Record<string, unknown>>
  public deployedChecked = false
  public stagedChecked = false
  public deployedDeploys$: BehaviorSubject<{
    deployedTactics: string[]
    deployedTechniques: string[]
  } | null>
  public stagedDeploys$: BehaviorSubject<{
    stagedTactics: string[]
    stagedTechniques: string[]
  } | null>

  public ngOnInit(): void {
    this.cyborgMitreFilter$ = this.huntPackageService.getMITREAggregations()
    this.tactics$ =
      cachedMitreSubject ??
      (this.httpClient
        .get(MitreComponent.mitreEnterpriseJsonUrl)
        .pipe(map(processMitreEnterpriseJSON), shareReplay(1)) as ReplaySubject<
        MITRETactics[]
      >)

    cachedMitreSubject = this.tactics$

    this.rows$ = this.tactics$.pipe(
      map((tactics) => {
        return new Array(
          Math.max(...tactics.map((tactic) => tactic.children.length)),
        )
          .fill(0)
          .map((_, i) => i)
      }),
    )

    this.deployedDeploys$ = new BehaviorSubject(null)
    this.stagedDeploys$ = new BehaviorSubject(null)
  }

  private getDeploysFilters(): Observable<DeployedDeploys> {
    return this.companyService.getDeployedDeploys(1000, 1).pipe(
      map((data) => {
        return {
          deployedTactics: data.items
            .map((huntPackage) => {
              return huntPackage.huntPackage.mapping.mitre.mitre_attack_tactics
            })
            .flat(),
          deployedTechniques: data.items
            .map((huntPackage) => {
              return huntPackage.huntPackage.mapping.mitre
                .mitre_attack_techniques
            })
            .flat(),
        }
      }),
    )
  }

  private getStagedFilters(): Observable<StagedDeploys> {
    return this.companyService.getStagedDeploys(1000, 1).pipe(
      map((data) => {
        return {
          stagedTactics: data.items
            .map((huntPackage) => {
              return huntPackage.huntPackage.mapping.mitre.mitre_attack_tactics
            })
            .flat(),
          stagedTechniques: data.items
            .map((huntPackage) => {
              return huntPackage.huntPackage.mapping.mitre
                .mitre_attack_techniques
            })
            .flat(),
        }
      }),
    )
  }

  public toggleDeployedDeploys = (): void => {
    if (this.deployedChecked) {
      this.getDeploysFilters().subscribe((data) => {
        this.deployedDeploys$.next(data)
      })
    } else {
      this.deployedDeploys$.next(null)
    }
  }

  public toggleStagedDeploys = (): void => {
    if (this.stagedChecked) {
      this.getStagedFilters().subscribe((data) => {
        this.stagedDeploys$.next(data)
      })
    } else {
      this.stagedDeploys$.next(null)
    }
  }
}
